import { TriangleAlert, X } from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import { forwardRef, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react';
import { cn } from '../lib/cn';
import { useControllableState } from '../lib/hooks';
import { Button } from './Button';
import { Input } from './Input';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

/** Max widths used by the Admin Console dialogs: 420 · 460 · 520 · 760. */
const DIALOG_WIDTH: Record<DialogSize, string> = {
  sm: 'max-w-[420px]',
  md: 'max-w-[460px]',
  lg: 'max-w-[520px]',
  xl: 'max-w-[760px]',
};

/**
 * Where focus goes when the dialog opens. Default: the heading (handoff accessibility notes).
 * `first` = first focusable element · `none` = leave focus alone (static previews only).
 */
export type InitialFocus = 'title' | 'first' | 'none' | RefObject<HTMLElement | null>;

export interface DialogLabels {
  close: string;
}

/** Options shared by Dialog, ConfirmDialog and Drawer. */
export interface OverlayBaseProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the overlay (focus returns to it on close). */
  trigger?: ReactElement;
  initialFocus?: InitialFocus;
  /**
   * Modal (default): focus trap, scroll lock, background hidden from assistive tech, scrim.
   * Non-modal renders without scrim/trap (e.g. inline previews).
   */
  modal?: boolean;
  /** Portal target (default document.body). Give the container `transform`/`contain: paint` to scope fixed positioning. */
  container?: HTMLElement | null;
}

export interface DialogProps extends OverlayBaseProps {
  title: ReactNode;
  description?: ReactNode;
  /** Body content (13px, 14px gaps, scrolls). */
  children?: ReactNode;
  /** Footer actions, right-aligned. Wrap cancel buttons in <DialogClose asChild>. */
  footer?: ReactNode;
  size?: DialogSize;
  /** Custom max-width (overrides `size`). */
  width?: number | string;
  hideClose?: boolean;
  /** When false, Escape and outside clicks do not close (use for unsaved-work flows). */
  dismissible?: boolean;
  labels?: Partial<DialogLabels>;
  className?: string;
  bodyClassName?: string;
}

export const DialogClose = RadixDialog.Close;

export function applyInitialFocus(
  initialFocus: InitialFocus,
  titleRef: RefObject<HTMLElement | null>,
  event: Event,
) {
  if (initialFocus === 'first') return;
  event.preventDefault();
  if (initialFocus === 'none') return;
  const target = initialFocus === 'title' ? titleRef.current : initialFocus.current;
  target?.focus();
}

export const OVERLAY_SCRIM =
  'fixed inset-0 z-50 bg-overlay data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out';

const CENTERER = 'fixed inset-0 z-50 grid place-items-center overflow-y-auto p-6';

const CLOSE_BTN =
  'grid size-[30px] shrink-0 place-items-center rounded-[6px] text-text-2 transition-colors hover:bg-surface-2 hover:text-text';

/**
 * Scrim + centring wrapper (modal) or a pointer-transparent centring wrapper (non-modal).
 * Forwards its ref: Radix's Portal/Presence attach refs to their direct children.
 */
const CenteredFrame = forwardRef<HTMLDivElement, { modal: boolean; children: ReactNode }>(
  function CenteredFrame({ modal, children, ...rest }, ref) {
    if (modal) {
      return (
        <RadixDialog.Overlay ref={ref} className={cn(OVERLAY_SCRIM, CENTERER)} {...rest}>
          {children}
        </RadixDialog.Overlay>
      );
    }
    return (
      <div ref={ref} className={cn(CENTERER, 'pointer-events-none [&>*]:pointer-events-auto')} {...rest}>
        {children}
      </div>
    );
  },
);

/**
 * Modal dialog (Radix Dialog): overlay, 12px radius, title 16/600, optional description,
 * scrolling body and right-aligned footer. Focus moves to the title on open and returns to the
 * trigger on close; Escape closes.
 */
export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    open,
    defaultOpen,
    onOpenChange,
    trigger,
    title,
    description,
    children,
    footer,
    size = 'lg',
    width,
    hideClose = false,
    initialFocus = 'title',
    dismissible = true,
    modal = true,
    container,
    labels,
    className,
    bodyClassName,
  },
  ref,
) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const l = { close: 'Close', ...labels };
  return (
    <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
      {trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
      <RadixDialog.Portal container={container}>
        <CenteredFrame modal={modal}>
          <RadixDialog.Content
            ref={ref}
            aria-modal={modal || undefined}
            {...(description ? {} : { 'aria-describedby': undefined })}
            onOpenAutoFocus={(event) => applyInitialFocus(initialFocus, titleRef, event)}
            onEscapeKeyDown={(event) => {
              if (!dismissible) event.preventDefault();
            }}
            onPointerDownOutside={(event) => {
              if (!dismissible) event.preventDefault();
            }}
            onInteractOutside={(event) => {
              if (!modal) event.preventDefault();
            }}
            style={width !== undefined ? { maxWidth: width } : undefined}
            className={cn(
              'relative flex max-h-[calc(100dvh-48px)] w-full flex-col overflow-hidden rounded-lg bg-surface text-text shadow-lg outline-none',
              'data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out',
              width === undefined && DIALOG_WIDTH[size],
              className,
            )}
          >
            <div className="flex shrink-0 items-start justify-between gap-3 px-5 pt-[18px]">
              <div className="min-w-0">
                <RadixDialog.Title
                  ref={titleRef}
                  tabIndex={-1}
                  className="text-16 font-semibold leading-6 outline-none focus-visible:shadow-none"
                >
                  {title}
                </RadixDialog.Title>
                {description ? (
                  <RadixDialog.Description className="mt-1 text-13 leading-[19px] text-text-2">
                    {description}
                  </RadixDialog.Description>
                ) : null}
              </div>
              {hideClose ? null : (
                <RadixDialog.Close aria-label={l.close} className={cn(CLOSE_BTN, '-me-1.5 -mt-1')}>
                  <X aria-hidden className="size-4" strokeWidth={2} />
                </RadixDialog.Close>
              )}
            </div>
            {children !== undefined && children !== null ? (
              <div
                className={cn(
                  'flex min-h-0 flex-1 flex-col gap-3.5 overflow-auto px-5 py-4 text-13',
                  bodyClassName,
                )}
              >
                {children}
              </div>
            ) : (
              <div className="h-4 shrink-0" />
            )}
            {footer ? (
              <div className="flex shrink-0 flex-wrap justify-end gap-2 px-5 pb-[18px]">{footer}</div>
            ) : null}
          </RadixDialog.Content>
        </CenteredFrame>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
});

/** Alias of {@link Dialog}. */
export const Modal = Dialog;

/* ------------------------------------------------------------------------------------------------
 * ConfirmDialog — destructive / confirm-first alert dialog
 * ---------------------------------------------------------------------------------------------- */

export interface ConfirmDialogLabels {
  cancel: ReactNode;
  /** "Type DELETE GLOBEX to confirm". */
  typeToConfirm: (text: string) => ReactNode;
}

const CONFIRM_LABELS: ConfirmDialogLabels = {
  cancel: 'Cancel',
  typeToConfirm: (text) => (
    <>
      Type <b>{text}</b> to confirm
    </>
  ),
};

export interface ConfirmDialogProps extends Omit<OverlayBaseProps, 'initialFocus'> {
  title: ReactNode;
  description?: ReactNode;
  /** Extra content between the text and the actions (e.g. an impact summary box). */
  children?: ReactNode;
  /** `danger` (default): red confirm button + warning tile. `primary`: neutral confirmation. */
  tone?: 'danger' | 'primary';
  /** Icon tile; `false` hides it. Defaults to the triangle-alert tile. */
  icon?: ReactNode | false;
  confirmLabel: ReactNode;
  /**
   * Called on confirm. Return a promise to keep the dialog open with a loading button until it
   * settles (it closes on success, stays open on rejection).
   */
  onConfirm: () => void | Promise<unknown>;
  /** Type-to-confirm: the confirm button stays disabled until this exact text is typed. */
  confirmText?: string;
  /** External loading flag for the confirm button. */
  loading?: boolean;
  /** Where focus starts: `cancel` (default — the safe action), `title` or `none`. */
  initialFocus?: 'cancel' | 'title' | 'none';
  labels?: Partial<ConfirmDialogLabels>;
  className?: string;
}

/**
 * Confirm-first alert dialog (role="alertdialog"). Focus starts on Cancel, outside clicks do not
 * dismiss it, the destructive action is red and can require typing a confirmation phrase.
 */
export const ConfirmDialog = forwardRef<HTMLDivElement, ConfirmDialogProps>(function ConfirmDialog(
  {
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    trigger,
    modal = true,
    container,
    initialFocus = 'cancel',
    title,
    description,
    children,
    tone = 'danger',
    icon,
    confirmLabel,
    onConfirm,
    confirmText,
    loading = false,
    labels,
    className,
  },
  ref,
) {
  const l = { ...CONFIRM_LABELS, ...labels };
  const [open, setOpen] = useControllableState({
    value: openProp,
    defaultValue: defaultOpen,
    onChange: onOpenChange,
  });
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const matches = confirmText === undefined || typed.trim() === confirmText;

  const handleOpenChange = (next: boolean) => {
    if (!next) setTyped('');
    setOpen(next);
  };

  const confirm = async () => {
    if (!matches || busy || loading) return;
    const result = onConfirm();
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setBusy(true);
      try {
        await result;
        handleOpenChange(false);
      } catch {
        // keep the dialog open so the user can retry
      } finally {
        setBusy(false);
      }
    } else {
      handleOpenChange(false);
    }
  };

  const tile =
    icon === false ? null : (
      <span
        aria-hidden
        className={cn(
          'grid size-10 shrink-0 place-items-center rounded-[10px]',
          tone === 'danger' ? 'bg-error-soft text-error' : 'bg-primary-soft text-primary',
        )}
      >
        {icon ?? <TriangleAlert className="size-5" strokeWidth={2} />}
      </span>
    );

  return (
    <RadixDialog.Root open={open} onOpenChange={handleOpenChange} modal={modal}>
      {trigger ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
      <RadixDialog.Portal container={container}>
        <CenteredFrame modal={modal}>
          <RadixDialog.Content
            ref={ref}
            role="alertdialog"
            aria-modal={modal || undefined}
            {...(description ? {} : { 'aria-describedby': undefined })}
            onOpenAutoFocus={(event) =>
              applyInitialFocus(initialFocus === 'cancel' ? cancelRef : initialFocus, titleRef, event)
            }
            onPointerDownOutside={(event) => event.preventDefault()}
            onInteractOutside={(event) => event.preventDefault()}
            className={cn(
              'flex w-full max-w-[400px] flex-col gap-3.5 rounded-lg bg-surface p-6 text-text shadow-lg outline-none',
              'data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out',
              className,
            )}
          >
            {tile}
            <div>
              <RadixDialog.Title
                ref={titleRef}
                tabIndex={-1}
                className="text-16 font-semibold leading-6 outline-none focus-visible:shadow-none"
              >
                {title}
              </RadixDialog.Title>
              {description ? (
                <RadixDialog.Description className="mt-1 text-13 leading-[19px] text-text-2">
                  {description}
                </RadixDialog.Description>
              ) : null}
            </div>
            {children}
            {confirmText !== undefined ? (
              <Input
                label={<span className="font-normal">{l.typeToConfirm(confirmText)}</span>}
                mono
                autoComplete="off"
                spellCheck={false}
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void confirm();
                  }
                }}
              />
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <RadixDialog.Close asChild>
                <Button ref={cancelRef} variant="secondary">
                  {l.cancel}
                </Button>
              </RadixDialog.Close>
              <Button
                variant={tone === 'danger' ? 'danger' : 'primary'}
                disabled={!matches}
                loading={busy || loading}
                onClick={() => void confirm()}
              >
                {confirmLabel}
              </Button>
            </div>
          </RadixDialog.Content>
        </CenteredFrame>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
});
